/**
 * MODULE: domain/sales-intelligence/frontend/api/client.ts
 * PURPOSE: API client for sales intelligence backend
 * 
 * DEPENDENCIES:
 *   - axios: HTTP client
 *   - _shared/lib/frontend/api/config: API_CONFIG (baseURL for gateway)
 *   - ../../contracts: Type definitions
 * 
 * PUBLIC API:
 *   - salesIntelligenceApi: listInsights (with filters), getInsight, updateInsight, deleteInsight,
 *     bulkDeleteInsights, exportCsv, listActivities, createActivity, startResearch, getWorkflowFields, etc.
 *   - 2026-02-10: updateSummary, addInsight, updateInsightItem, deleteInsightItem, addBuyingSignal, addPainPoint
 * 
 * SIDE EFFECTS:
 *   - HTTP requests to backend API
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 649 / 700 lines
 *
 * Recent: 2026-05-21 — [2.2.1085] Request interceptor synthetic **429** uses **`AxiosError['response']`** cast (fixes shell **`tsc`** on **`brazn-dev`** / **`main`**). 2026-05-08 — [2.2.874] **`bumpSalesIntel429Backoff`** also arms **`gatewayRateLimit`** (see **`salesIntelRateLimit.ts`**). **429** response interceptor arms **`bumpSalesIntel429Backoff`** (**Retry-After**) before reject so **`useInsights`** skips HTTP while gateway window resets. [2.2.778] **`skipSalesIntel429Retry`** GETs (`listInsights`, `getInsight`, `exportCsv`).
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import axios, { AxiosInstance, AxiosError, type AxiosRequestConfig } from 'axios';

/** Read GETs pass this so the response interceptor skips 429 exponential retries (hooks or gateway quotas). */
type SalesIntelAxiosConfig = AxiosRequestConfig & { skipSalesIntel429Retry?: boolean };
import { API_CONFIG } from '../../../../_shared/lib/frontend/api/config';
import { bumpSalesIntel429Backoff, isInSalesIntelBackoff } from './salesIntelRateLimit';
import { isInGatewayBackoff } from '../../../../_shared/lib/frontend/api/gatewayRateLimit';
import {
  ResearchRequest,
  ResearchResponse,
  InsightResponse,
  CompanyInsight,
  WorkflowStatusResponse,
  CheckContextResponse,
  SaveQuickSetupRequest,
  SaveQuickSetupResponse,
  SellerCompanyProfile
} from '../../contracts/sales-intelligence.types';

/** User-facing message when backend is unreachable (Fix #4: frontend resilience). */
export const SALES_INTELLIGENCE_SERVICE_UNAVAILABLE =
  'Sales Intelligence service is temporarily unavailable. Please try again later.';

function isNetworkError(err: AxiosError): boolean {
  const code = (err as AxiosError & { code?: string }).code;
  const message = (err.message || '').toLowerCase();
  return (
    code === 'ECONNREFUSED' ||
    code === 'ETIMEDOUT' ||
    code === 'ERR_NETWORK' ||
    message.includes('network error') ||
    message.includes('connection refused')
  );
}

export interface InsightUpdateData {
  company_name?: string;
  research_data?: Record<string, unknown>;
  key_insights?: string[];
  signals?: Record<string, unknown>;
  lead_score?: number;
  priority_tier?: 'hot' | 'warm' | 'cold';
}

// =====================================================
// API Client Class
// =====================================================

class SalesIntelligenceApiClient {
  private client: AxiosInstance;
  
  constructor(baseURL?: string) {
    const resolvedBase =
      baseURL ?? (API_CONFIG.baseURL ? `${API_CONFIG.baseURL}/api/v1/sales-intelligence` : '/api/v1/sales-intelligence');
    this.client = axios.create({
      baseURL: resolvedBase,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    // Add auth interceptor; skip outbound calls while gateway/SI 429 backoff is active
    this.client.interceptors.request.use((config) => {
      if (isInGatewayBackoff() || isInSalesIntelBackoff()) {
        const err = new AxiosError(
          'Sales Intelligence requests paused (rate limit cooldown)',
          'ERR_CANCELED',
          config,
        );
        err.response = { status: 429, data: {} } as AxiosError['response'];
        return Promise.reject(err);
      }
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Error interceptor: exponential backoff on 429, then one retry for transient network errors (Fix #4)
    this.client.interceptors.response.use(
      (res) => res,
      async (err: AxiosError) => {
        const config = err.config as (typeof err.config) & {
          _retried?: boolean;
          _rateLimitRetries?: number;
          /** When true, do not retry 429 — list/detail hooks already backoff or mock; retries multiply GETs against gateway quotas. */
          skipSalesIntel429Retry?: boolean;
        };
        const status = err.response?.status;
        if (status === 429) {
          bumpSalesIntel429Backoff(err);
        }
        // ADR: bounded retries on rate limit for mutating or non-flagged calls — read GETs opt out via skipSalesIntel429Retry
        if (
          status === 429 &&
          config &&
          !config.skipSalesIntel429Retry &&
          (config._rateLimitRetries ?? 0) < 3
        ) {
          const n = config._rateLimitRetries ?? 0;
          config._rateLimitRetries = n + 1;
          const delayMs = Math.pow(2, n) * 1000;
          await new Promise((r) => setTimeout(r, delayMs));
          return this.client.request(config);
        }
        if (isNetworkError(err) && config && !config._retried) {
          config._retried = true;
          await new Promise((r) => setTimeout(r, 1000));
          return this.client.request(config);
        }
        if (isNetworkError(err)) {
          const friendly = new Error(SALES_INTELLIGENCE_SERVICE_UNAVAILABLE) as Error & {
            code?: string;
            isNetworkError?: boolean;
          };
          friendly.code = (err as AxiosError & { code?: string }).code;
          friendly.isNetworkError = true;
          throw friendly;
        }
        if (err.response?.data && typeof err.response.data === 'object' && 'detail' in (err.response.data as object)) {
          const detail = (err.response.data as { detail?: string }).detail;
          const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
          throw new Error(msg || err.message);
        }
        throw err;
      }
    );
  }
  
  /**
   * Start company research workflow
   */
  async startResearch(request: ResearchRequest): Promise<ResearchResponse> {
    const response = await this.client.post<ResearchResponse>('/research', request);
    return response.data;
  }
  
  /**
   * Get insight for a company
   */
  async getInsight(companyName: string): Promise<InsightResponse> {
    const response = await this.client.get<InsightResponse>(
      `/insights/${encodeURIComponent(companyName)}`,
      { skipSalesIntel429Retry: true } as SalesIntelAxiosConfig,
    );
    return response.data;
  }
  
  /**
   * Create a manual insight (account) without running research.
   */
  async createInsight(data: {
    company_name: string;
    website?: string;
    industry?: string;
    priority?: string;
    source?: string;
    notes?: string;
  }): Promise<CompanyInsight> {
    const response = await this.client.post<CompanyInsight>('/insights', data);
    return response.data;
  }

  /**
   * List insights with optional filters. Returns paginated result.
   */
  async listInsights(params?: {
    limit?: number;
    offset?: number;
    min_score?: number;
    max_score?: number;
    priority?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<{ insights: CompanyInsight[]; total: number; limit: number; offset: number }> {
    const response = await this.client.get<{
      insights: CompanyInsight[];
      total: number;
      limit: number;
      offset: number;
    }>('/insights', { params, skipSalesIntel429Retry: true } as SalesIntelAxiosConfig);
    return response.data;
  }

  /**
   * Update an existing insight
   */
  async updateInsight(
    insightId: string,
    data: InsightUpdateData
  ): Promise<{ success: boolean; insight: CompanyInsight }> {
    const response = await this.client.patch<{ success: boolean; insight: CompanyInsight }>(
      `/insights/${insightId}`,
      data
    );
    return response.data;
  }

  /**
   * Delete an insight
   */
  async deleteInsight(insightId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.client.delete<{ success: boolean; message: string }>(
      `/insights/${insightId}`
    );
    return response.data;
  }

  // -----------------------------------------
  // Insight edits (Enhanced Research Tab)
  // -----------------------------------------

  async updateInsightSummary(insightId: string, summary: string, reason?: string) {
    const response = await this.client.patch<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/summary`,
      { summary, reason }
    );
    return response.data;
  }

  async addInsightItem(insightId: string, text: string, source = 'human_added') {
    const response = await this.client.post<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/insights`,
      { text, source }
    );
    return response.data;
  }

  async updateInsightItem(insightId: string, itemId: string, text: string, reason?: string) {
    const response = await this.client.patch<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/insights`,
      { item_id: itemId, text, reason }
    );
    return response.data;
  }

  async deleteInsightItem(insightId: string, itemId: string) {
    const response = await this.client.delete<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/insights`,
      { data: { item_id: itemId } }
    );
    return response.data;
  }

  async addBuyingSignal(insightId: string, text: string, strength: 'strong' | 'medium' | 'weak' = 'medium') {
    const response = await this.client.post<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/buying-signals`,
      { text, strength }
    );
    return response.data;
  }

  async addPainPoint(insightId: string, text: string, severity: 'high' | 'medium' | 'low' = 'medium', evidence: string[] = []) {
    const response = await this.client.post<{ success: boolean; data: CompanyInsight }>(
      `/insights/${insightId}/edits/pain-points`,
      { text, severity, evidence }
    );
    return response.data;
  }

  /**
   * Export insight as PDF (returns blob for download)
   */
  async exportToPDF(insightId: string, includeMetadata = true): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/insights/${insightId}/export/pdf`,
      {
        params: { include_metadata: includeMetadata },
        responseType: 'blob',
      }
    );
    return response.data;
  }

  /**
   * Export insight as JSON (returns blob for download)
   */
  async exportToJSON(insightId: string): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/insights/${insightId}/export/json`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Export insight as Markdown (returns blob for download)
   */
  async exportToMarkdown(insightId: string): Promise<Blob> {
    const response = await this.client.get<Blob>(
      `/insights/${insightId}/export/markdown`,
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * List activities for an insight
   */
  async listActivities(insightId: string): Promise<{ activities: Array<{
    id: string;
    activity_type: string;
    notes: string;
    metadata?: Record<string, unknown>;
    created_at: string;
    created_by: string;
  }> }> {
    const response = await this.client.get<{ activities: Array<{
      id: string;
      activity_type: string;
      notes: string;
      metadata?: Record<string, unknown>;
      created_at: string;
      created_by: string;
    }> }>(`/insights/${insightId}/activities`);
    return response.data;
  }

  /**
   * Create activity for an insight
   */
  async createActivity(
    insightId: string,
    data: { activity_type: string; notes: string; metadata?: Record<string, unknown> }
  ): Promise<{ success: boolean; activity?: Record<string, unknown> }> {
    const response = await this.client.post<{ success: boolean; activity?: Record<string, unknown> }>(
      `/insights/${insightId}/activities`,
      data
    );
    return response.data;
  }

  /**
   * Export insights as CSV (returns blob for download).
   */
  async exportCsv(): Promise<Blob> {
    const response = await this.client.get('/insights/export', {
      responseType: 'blob',
      skipSalesIntel429Retry: true,
    } as SalesIntelAxiosConfig);
    return response.data;
  }

  /**
   * Bulk delete insights
   */
  async bulkDeleteInsights(insightIds: string[]): Promise<{
    success: boolean;
    deleted_count: number;
    requested_count: number;
  }> {
    const response = await this.client.post<{
      success: boolean;
      deleted_count: number;
      requested_count: number;
    }>('/insights/bulk-delete', { insight_ids: insightIds });
    return response.data;
  }
  
  /**
   * Check workflow status (polling for real-time updates)
   */
  async getWorkflowStatus(workflowId: string): Promise<WorkflowStatusResponse> {
    const response = await this.client.get<WorkflowStatusResponse>(
      `/workflows/${workflowId}/status`
    );
    return response.data;
  }
  
  /**
   * Health check
   */
  async healthCheck(): Promise<{ status: string; service: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  /**
   * Get merged workflow fields (schema + tenant config)
   */
  async getWorkflowFields(
    workflowType: string,
    includeDisabled = false
  ): Promise<WorkflowFieldConfig[]> {
    const response = await this.client.get<WorkflowFieldConfig[]>(
      `/workflows/${workflowType}/fields`,
      { params: { include_disabled: includeDisabled } }
    );
    return response.data;
  }

  /** Enabled fields only (for research stream / agent prompt). */
  async getEnabledFields(workflowType: string): Promise<WorkflowFieldConfig[]> {
    const response = await this.client.get<WorkflowFieldConfig[]>(
      `/workflows/${workflowType}/fields`,
      { params: { enabled_only: true } }
    );
    return response.data;
  }

  /**
   * Persist a user-edited scalar research field (stored in user_overrides; survives re-research).
   */
  async updateResearchField(
    insightId: string,
    fieldKey: string,
    value: unknown
  ): Promise<{ success: boolean; field: string; value: unknown; insight: CompanyInsight }> {
    const response = await this.client.patch<{
      success: boolean;
      field: string;
      value: unknown;
      insight: CompanyInsight;
    }>(`/insights/${insightId}/fields`, { field_key: fieldKey, value });
    return response.data;
  }

  async createCustomField(payload: {
    workflow_type: string;
    field_label: string;
    description: string;
    field_type: string;
  }): Promise<{ success: boolean; field_key: string; field?: Record<string, unknown> }> {
    const { workflow_type, field_label, description, field_type } = payload;
    const response = await this.client.post<{
      success: boolean;
      field_key: string;
      field?: Record<string, unknown>;
    }>(`/workflows/${workflow_type}/fields/custom`, {
      field_label,
      description,
      field_type,
    });
    return response.data;
  }

  /**
   * Update tenant field config
   */
  async updateFieldConfig(
    workflowType: string,
    fieldKey: string,
    updates: FieldConfigUpdate
  ): Promise<unknown> {
    const response = await this.client.patch<unknown>(
      `/workflows/${workflowType}/fields/${fieldKey}`,
      updates
    );
    return response.data;
  }

  /**
   * Batch reorder fields
   */
  async reorderFields(
    workflowType: string,
    request: { field_orders: Array<{ field_key: string; ui_order: number }> }
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.client.post<{ success: boolean; message: string }>(
      `/workflows/${workflowType}/fields/reorder`,
      request
    );
    return response.data;
  }

  /**
   * Get extraction prompt + schema (for testing)
   */
  async getExtractionPrompt(
    workflowType: string,
    companyName: string
  ): Promise<{ prompt: string; schema: Record<string, unknown> }> {
    const response = await this.client.get<{ prompt: string; schema: Record<string, unknown> }>(
      `/workflows/${workflowType}/extraction-prompt`,
      { params: { company_name: companyName } }
    );
    return response.data;
  }

  // -----------------------------------------
  // Seller context (context-aware research)
  // -----------------------------------------

  async checkContext(): Promise<CheckContextResponse> {
    const response = await this.client.get<CheckContextResponse>('/context/check');
    return response.data;
  }

  async saveQuickSetup(data: Omit<SaveQuickSetupRequest, 'tenantId'>): Promise<SaveQuickSetupResponse> {
    const response = await this.client.post<SaveQuickSetupResponse>('/context/quick-setup', data);
    return response.data;
  }

  /** JIT context: save quick setup with snake_case payload (new backend). */
  async saveQuickSetupContext(payload: {
    company_name: string;
    value_prop: string;
    target_industries: string[];
    ideal_size: string;
    pain_points?: string[];
    integrations?: string[];
    competitors?: string[];
  }): Promise<{ success: boolean; profile_id: string; company_name: string; tenant_id: string }> {
    const response = await this.client.post<{
      success: boolean;
      profile_id: string;
      company_name: string;
      tenant_id: string;
    }>('/context/quick-setup', payload);
    return response.data;
  }

  async getContext(): Promise<SellerCompanyProfile> {
    const response = await this.client.get<SellerCompanyProfile>('/context');
    return response.data;
  }

  async getSellerProfile(): Promise<SellerCompanyProfile> {
    const response = await this.client.get<SellerCompanyProfile>('/context/profile');
    return response.data;
  }

  async deleteSellerProfile(): Promise<void> {
    await this.client.delete('/context/profile');
  }

  async updateContext(updates: Partial<SellerCompanyProfile>): Promise<SellerCompanyProfile> {
    const response = await this.client.patch<SellerCompanyProfile>('/context', updates);
    return response.data;
  }

  // -----------------------------------------
  // Accounts (save insight to accounts, list)
  // -----------------------------------------

  async saveToAccount(data: {
    insightId: string;
    accountName?: string;
    tags?: string[];
  }): Promise<{ success: boolean; accountId: string; accountName: string; message: string }> {
    const response = await this.client.post<{
      success: boolean;
      accountId: string;
      accountName: string;
      message: string;
    }>('/accounts/save', {
      insightId: data.insightId,
      accountName: data.accountName,
      tags: data.tags ?? []
    });
    return response.data;
  }

  async listAccounts(): Promise<{
    accounts: Array<{
      id: string;
      name: string;
      fitScore: number | null;
      priorityTier: string | null;
      tags: string[];
      createdAt: string;
      updatedAt: string;
      hasInsight: boolean;
    }>;
  }> {
    const response = await this.client.get<{
      accounts: Array<{
        id: string;
        name: string;
        fitScore: number | null;
        priorityTier: string | null;
        tags: string[];
        createdAt: string;
        updatedAt: string;
        hasInsight: boolean;
      }>;
    }>('/accounts/list');
    return response.data;
  }
}

export interface WorkflowFieldConfig {
  field_key: string;
  field_label: string;
  field_type: string;
  description: string;
  is_required: boolean;
  is_input: boolean;
  is_output: boolean;
  is_enabled: boolean;
  is_visible_in_ui: boolean;
  priority: number;
  extraction_weight: number;
  ai_prompt: string | null;
  field_group: string;
  ui_order: number;
  options?: string[];
  validation_rules?: Record<string, unknown>;
  /** True when created via Add custom field (tenant-defined). */
  is_custom?: boolean;
}

export interface FieldConfigUpdate {
  is_enabled?: boolean;
  is_visible_in_ui?: boolean;
  custom_label?: string;
  priority?: number;
  extraction_weight?: number;
  ui_display_order?: number;
}

// =====================================================
// Export singleton instance
// =====================================================

export const salesIntelligenceApi = new SalesIntelligenceApiClient();

// Seller context API (same client, convenience export for hooks)
export const sellerContextApi = {
  check: () => salesIntelligenceApi.checkContext(),
  quickSetup: (data: Omit<SaveQuickSetupRequest, 'tenantId'>) => salesIntelligenceApi.saveQuickSetup(data),
  get: () => salesIntelligenceApi.getContext(),
  getProfile: () => salesIntelligenceApi.getSellerProfile(),
  deleteProfile: () => salesIntelligenceApi.deleteSellerProfile(),
  update: (updates: Partial<SellerCompanyProfile>) => salesIntelligenceApi.updateContext(updates)
};

// Accounts API
export const accountsApi = {
  save: (data: { insightId: string; accountName?: string; tags?: string[] }) =>
    salesIntelligenceApi.saveToAccount(data),
  list: () => salesIntelligenceApi.listAccounts()
};